import { activeTenant } from '../../lib/tenant'
import { DEFAULT_SITE_CONTENT, type SiteContent } from './siteContent'

function setText(id: string, value: string) {
  const node = document.getElementById(id)
  if (node) node.textContent = value
}

function normalizeCandidates(...values: string[]) {
  return [...new Set(values.map(value => value.trim()).filter(Boolean))]
}

function setImageCandidates(
  image: HTMLImageElement,
  candidates: string[],
  alt = '',
) {
  const sources = normalizeCandidates(...candidates)
  image.alt = alt
  image.hidden = false
  image.dataset.mediaState = 'loading'
  image.style.visibility = 'hidden'
  image.style.opacity = '0'
  image.removeAttribute('src')

  const reveal = () => {
    image.dataset.mediaState = 'ready'
    image.style.visibility = 'visible'
    image.style.opacity = '1'
  }

  const clear = () => {
    image.dataset.mediaState = 'empty'
    image.style.visibility = 'hidden'
    image.style.opacity = '0'
    image.removeAttribute('src')
    image.onload = null
    image.onerror = null
  }

  if (!sources.length) {
    clear()
    return
  }

  let index = 0
  const tryNext = () => {
    const source = sources[index++]
    if (!source) {
      clear()
      return
    }

    image.dataset.mediaState = 'loading'
    image.style.visibility = 'hidden'
    image.style.opacity = '0'

    image.onload = () => {
      if (image.naturalWidth > 0 && image.naturalHeight > 0) reveal()
      else tryNext()
    }
    image.onerror = () => {
      tryNext()
    }

    image.src = source

    // Cached images can already be complete before some browsers dispatch load.
    if (image.complete) {
      queueMicrotask(() => {
        if (image.src !== source) return
        if (image.naturalWidth > 0 && image.naturalHeight > 0) reveal()
        else tryNext()
      })
    }
  }

  tryNext()
}

function setImageCandidatesById(
  id: string,
  candidates: string[],
  alt = '',
) {
  const image = document.getElementById(id) as HTMLImageElement | null
  if (image) setImageCandidates(image, candidates, alt)
}

function setLogoGroup(
  selector: string,
  candidates: string[],
  alt: string,
) {
  document.querySelectorAll<HTMLImageElement>(selector).forEach(image => {
    setImageCandidates(image, candidates, alt)
  })
}

function safeSocialUrl(kind: 'whatsapp' | 'instagram' | 'linkedin', value: string) {
  const raw = value.trim()
  if (!raw) return ''

  if (kind === 'whatsapp' && !/^https?:\/\//i.test(raw)) {
    const digits = raw.replace(/\D/g, '')
    return digits ? `https://wa.me/${digits}` : ''
  }

  try {
    const url = new URL(raw)
    return url.protocol === 'https:' || url.protocol === 'http:' ? url.toString() : ''
  } catch {
    return ''
  }
}

function hydrateSocialLinks(content: SiteContent) {
  const values = {
    whatsapp: safeSocialUrl('whatsapp', content.social.whatsapp || content.contact.whatsapp),
    instagram: safeSocialUrl('instagram', content.social.instagram),
    linkedin: safeSocialUrl('linkedin', content.social.linkedin),
  }

  document.querySelectorAll<HTMLAnchorElement>('[data-social]').forEach(link => {
    const kind = link.dataset.social as keyof typeof values | undefined
    const href = kind ? values[kind] : ''
    link.hidden = !href
    if (href) {
      link.href = href
      link.target = '_blank'
      link.rel = 'noopener noreferrer'
    } else {
      link.removeAttribute('href')
    }
  })

  const group = document.getElementById('siteSocialLinks')
  if (group) group.hidden = !Object.values(values).some(Boolean)
}

function setOptionalText(id: string, value: string, fallback = '') {
  setText(id, value.trim() || fallback)
}

function upsertMeta(attribute: 'name' | 'property', key: string, content: string) {
  let node = document.head.querySelector<HTMLMetaElement>(`meta[${attribute}="${key}"]`)
  if (!content) {
    node?.remove()
    return
  }
  if (!node) {
    node = document.createElement('meta')
    node.setAttribute(attribute, key)
    document.head.appendChild(node)
  }
  node.content = content
}

function upsertLink(rel: string, href: string, options?: { sizes?: string; type?: string }) {
  let selector = `link[rel="${rel}"]`
  if (options?.sizes) selector += `[sizes="${options.sizes}"]`
  let node = document.head.querySelector<HTMLLinkElement>(selector)
  if (!href) {
    node?.remove()
    return
  }
  if (!node) {
    node = document.createElement('link')
    node.rel = rel
    if (options?.sizes) node.setAttribute('sizes', options.sizes)
    if (options?.type) node.type = options.type
    document.head.appendChild(node)
  }
  node.href = href
  if (options?.type) node.type = options.type
}

function upsertJsonLd(id: string, value: Record<string, unknown> | null) {
  let node = document.getElementById(id) as HTMLScriptElement | null
  if (!value) {
    node?.remove()
    return
  }
  if (!node) {
    node = document.createElement('script')
    node.id = id
    node.type = 'application/ld+json'
    document.head.appendChild(node)
  }
  node.textContent = JSON.stringify(value)
}

function hydrateWebMetadata(content: SiteContent, displayName: string, legalName: string, location: string) {
  const canonicalUrl = content.seo.canonicalUrl.trim() || window.location.origin + window.location.pathname
  const title = content.seo.siteName.trim() || displayName
  const description = content.seo.description.trim()
  const ogTitle = content.seo.ogTitle.trim() || title
  const ogDescription = content.seo.ogDescription.trim() || description
  const ogImage = content.seo.ogImageUrl.trim() || content.hero.imageUrl.trim() || content.brand.logoUrl.trim()
  const isPreview = window.location.hostname === 'localhost'
    || window.location.hostname === '127.0.0.1'
    || window.location.hostname.endsWith('.workers.dev')
  const robots = isPreview ? 'noindex,nofollow' : (content.seo.robots.trim() || 'index,follow')
  const twitterCard = content.seo.twitterCard.trim() || 'summary_large_image'

  document.title = title
  upsertMeta('name', 'description', description)
  upsertMeta('name', 'keywords', content.seo.keywords.trim())
  upsertMeta('name', 'robots', robots)
  upsertMeta('name', 'theme-color', content.brand.primaryColor)
  upsertMeta('property', 'og:type', 'website')
  upsertMeta('property', 'og:locale', 'es_DO')
  upsertMeta('property', 'og:site_name', title)
  upsertMeta('property', 'og:title', ogTitle)
  upsertMeta('property', 'og:description', ogDescription)
  upsertMeta('property', 'og:url', canonicalUrl)
  upsertMeta('property', 'og:image', ogImage)
  upsertMeta('name', 'twitter:card', twitterCard)
  upsertMeta('name', 'twitter:title', ogTitle)
  upsertMeta('name', 'twitter:description', ogDescription)
  upsertMeta('name', 'twitter:image', ogImage)
  upsertLink('canonical', canonicalUrl)

  const favicon = content.brand.faviconUrl.trim() || content.brand.logoMarkUrl.trim() || content.brand.logoUrl.trim()
  const appleTouchIcon = content.brand.appleTouchIconUrl.trim() || favicon
  upsertLink('icon', favicon)
  upsertLink('apple-touch-icon', appleTouchIcon)

  const sameAs = [
    content.social.instagram,
    content.social.linkedin,
    safeSocialUrl('whatsapp', content.social.whatsapp || content.contact.whatsapp),
  ].map(value => value.trim()).filter(Boolean)

  upsertJsonLd('site-organization-schema', {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: displayName || legalName,
    legalName: legalName || undefined,
    url: canonicalUrl,
    logo: content.brand.logoUrl || favicon || undefined,
    image: ogImage || undefined,
    email: content.contact.email || undefined,
    telephone: content.contact.phone || undefined,
    address: content.contact.address || location || undefined,
    sameAs: sameAs.length ? sameAs : undefined,
  })
}

export function hydrateSiteContent(content: SiteContent) {
  ;(window as typeof window & { __webappContent?: SiteContent }).__webappContent = content

  document.documentElement.style.setProperty('--acc-navy', content.brand.primaryColor)
  document.documentElement.style.setProperty('--acc-gold', content.brand.accentColor)

  const displayName = content.identity.displayName.trim() || content.seo.siteName.trim()
  const legalName = content.identity.legalName.trim() || displayName
  const location = content.identity.shortLocation.trim() || content.contact.address.trim()
  const legalEmail = content.footer.legalEmail.trim() || content.contact.email.trim()

  setText('siteHeaderName', displayName)
  setText('siteHeaderSubtitle', content.identity.headerSubtitle)
  setText('siteFooterName', displayName)
  setText('siteFooterSubtitle', content.identity.headerSubtitle)
  setText('siteFooterDescription', content.footer.description)
  setText('siteHeroEyebrow', content.hero.eyebrow)
  setText('siteHeroTitle', content.hero.title)
  setText('siteHeroDescription', content.hero.description)
  setText('siteHeroCta', content.hero.ctaLabel)
  setText('siteHeroLocation', location)
  setText('siteCtaEyebrow', content.cta.eyebrow)
  setText('siteCtaTitle', content.cta.title)
  setText('siteCtaDescription', content.cta.description)

  setText('reservationTraceabilityTitle', displayName
    ? `Claro para el cliente. Trazable para ${displayName}.`
    : 'Claro para el cliente. Proceso trazable.')
  setOptionalText(
    'reservationPaymentInstruction',
    content.reservation.instructions,
    'Sigue las instrucciones de pago publicadas para continuar con tu reserva.',
  )
  setText(
    'reservationValidationText',
    displayName
      ? `Sube tu comprobante. ${displayName} valida el pago y actualiza tu expediente.`
      : 'Sube tu comprobante para validación y seguimiento.',
  )
  setText(
    'reservationProviderLabel',
    content.reservation.paymentProviderLabel.trim()
      ? `Proveedor: ${content.reservation.paymentProviderLabel.trim()}`
      : 'Proveedor de pago según instrucciones vigentes',
  )

  const year = String(new Date().getFullYear())
  const footerParts = [
    `© ${year} ${legalName}`,
    location,
    legalEmail,
  ].filter(Boolean)
  setText('siteFooterLegalLine', footerParts.join(' · '))
  setText('appFooterLegalLine', footerParts.filter((_, index) => index !== 1).join(' · '))

  setText('brokerRegisterIntro', displayName
    ? `Completa tus datos. ${displayName} validará la solicitud y te enviará el acceso cuando la cuenta sea aprobada.`
    : 'Completa tus datos. La solicitud será validada antes de habilitar el acceso.')
  setText('brokerPolicyTitle', content.brokerPortal.registrationPolicyTitle)
  setText('brokerPolicyVersion', content.brokerPortal.registrationPolicyVersion
    ? `versión ${content.brokerPortal.registrationPolicyVersion}`
    : '')
  setText('brokerPolicyText', content.brokerPortal.registrationPolicyText)
  setText(
    'brokerPolicyConsentLabel',
    content.brokerPortal.registrationConsentLabel ||
      'He leído y acepto la política comercial vigente.',
  )
  setText('brokerValidationCopy', displayName
    ? `${displayName} revisa tus datos y la aceptación de política.`
    : 'Se revisan tus datos y la aceptación de política.')
  setText('kycConsentLabel', displayName
    ? `Autorizo a ${displayName} a utilizar estos datos para gestionar mi caso comercial y contactarme sobre esta solicitud.`
    : 'Autorizo el uso de estos datos para gestionar mi caso comercial y recibir contacto sobre esta solicitud.')
  setText('caseAuthorizationCopy', displayName
    ? `Consulta el estado de tu solicitud y, cuando ${displayName} lo autorice, envía el comprobante de separación.`
    : 'Consulta el estado de tu solicitud y envía el comprobante cuando sea autorizado.')
  setText('clientLoginBrand', displayName ? `${displayName} · Portal del cliente` : 'Portal del cliente')
  setText('brokerLoginBrand', displayName ? `${displayName} · Red comercial` : 'Red comercial')
  setText('brokerLoginCopy', displayName
    ? `Ingresa con el código y PIN habilitados por el equipo de ${displayName}.`
    : 'Ingresa con el código y PIN habilitados para tu cuenta.')
  setText('clientPortalCompanyLabel', displayName ? `Mi propiedad · ${displayName}` : 'Mi propiedad')

  const policyVersionInput = document.getElementById('brokerPolicyVersionInput') as HTMLInputElement | null
  if (policyVersionInput) {
    policyVersionInput.value = content.brokerPortal.registrationPolicyVersion
  }
  const brokerCompanyId = document.getElementById('brokerCompanyIdInput') as HTMLInputElement | null
  if (brokerCompanyId) {
    brokerCompanyId.value = activeTenant.companyId
  }

  setImageCandidatesById(
    'siteHeroImage',
    [content.hero.imageUrl],
    content.hero.title || displayName,
  )
  setImageCandidatesById(
    'siteHeroLogo',
    [
      content.brand.logoLightUrl,
      content.brand.logoMarkUrl,
      content.brand.logoUrl,
      DEFAULT_SITE_CONTENT.brand.logoLightUrl,
    ],
    displayName,
  )
  setLogoGroup(
    '[data-site-logo="main"]',
    [
      content.brand.logoUrl,
      content.brand.logoMarkUrl,
      content.brand.logoLightUrl,
      DEFAULT_SITE_CONTENT.brand.logoUrl,
    ],
    displayName,
  )
  setLogoGroup(
    '[data-site-logo="mark"]',
    [
      content.brand.logoMarkUrl,
      content.brand.logoUrl,
      content.brand.logoLightUrl,
      DEFAULT_SITE_CONTENT.brand.logoMarkUrl,
    ],
    displayName,
  )
  setLogoGroup(
    '[data-site-logo="light"]',
    [
      content.brand.logoLightUrl,
      content.brand.logoUrl,
      content.brand.logoMarkUrl,
      DEFAULT_SITE_CONTENT.brand.logoLightUrl,
    ],
    displayName,
  )
  hydrateSocialLinks(content)
  hydrateWebMetadata(content, displayName, legalName, location)
}
